import { DIRECTED_MAX_CAST_PER_SHOT, type DirectedCastEntry } from 'fym-shared/src/music24/directedVideoApi';
import type { DirectedCastLibraryEntry, MusicVideoEditorDirected } from 'fym-shared/src/music24/musicVideoApi';

/** The most reference pictures of one AI clip: Veo takes at most 3. */
export const AI_CLIP_MAX_REFERENCES = DIRECTED_MAX_CAST_PER_SHOT;

/**
 * The pictures that the form of a directed cut opens with (design i1): the cast entries that the
 * shots of the section use most, at most 3, as file ids. Equal counts keep the order of first use.
 * With no section, the shots of the whole song count. Pure.
 */
export function sectionCastFileIds(
  directed: MusicVideoEditorDirected,
  section: { startSec: number; endSec: number } | null
): number[] {
  const counts = new Map<string, number>();
  for (const shot of directed.shots) {
    const inSection = section === null || (shot.startSec < section.endSec && shot.endSec > section.startSec);
    if (!inSection) {
      continue;
    }
    for (const castId of shot.castIds) {
      counts.set(castId, (counts.get(castId) ?? 0) + 1);
    }
  }
  const ranked = [...counts.entries()].map(([castId, count], order) => ({ castId, count, order }));
  ranked.sort((a, b) => (b.count !== a.count ? b.count - a.count : a.order - b.order));
  const fileIds: number[] = [];
  for (const { castId } of ranked) {
    const entry = directed.cast.find((item) => item.id === castId);
    if (entry !== undefined && !fileIds.includes(entry.fileId)) {
      fileIds.push(entry.fileId);
    }
    if (fileIds.length >= AI_CLIP_MAX_REFERENCES) {
      break;
    }
  }
  return fileIds;
}

/** The picture of a file id: from the cast of the directed video, else from the library. Pure. */
export function referenceEntryOf(
  fileId: number,
  library: readonly DirectedCastLibraryEntry[],
  directedCast: readonly DirectedCastEntry[]
): DirectedCastEntry | null {
  return (
    directedCast.find((entry) => entry.fileId === fileId) ?? library.find((entry) => entry.fileId === fileId) ?? null
  );
}

/** The library grouped by the song of its directed video, in the order of the library (design i2). Pure. */
export function libraryGroups(
  library: readonly DirectedCastLibraryEntry[]
): Array<{ songTitle: string; entries: DirectedCastLibraryEntry[] }> {
  const groups: Array<{ songTitle: string; entries: DirectedCastLibraryEntry[] }> = [];
  for (const entry of library) {
    const group = groups.find((item) => item.songTitle === entry.songTitle);
    if (group === undefined) {
      groups.push({ songTitle: entry.songTitle, entries: [entry] });
    } else {
      group.entries.push(entry);
    }
  }
  return groups;
}

/** Adds a file id at the end, or removes it. A fourth pick does nothing. Pure. */
export function toggleReference(fileIds: readonly number[], fileId: number): number[] {
  if (fileIds.includes(fileId)) {
    return fileIds.filter((id) => id !== fileId);
  }
  return fileIds.length >= AI_CLIP_MAX_REFERENCES ? [...fileIds] : [...fileIds, fileId];
}
